import { useState, useRef, useCallback, useEffect } from "react";
import { parseAamvaBarcode, type AamvaBarcode } from "../utils/aamva/aamva";
import { guideInVideoPixels } from "../utils/mrz/mrzImage";
import {
  decodePdf417,
  isPdf417ReaderReady,
  pdf417ReaderMode,
  MAX_DECODE_WIDTH,
  SWEEP_ANGLES,
  WHOLE_FRAME_MAX_WIDTH,
  type DecodeVariant,
} from "../services/scan/pdf417Reader";

// EN-4743. Reads the PDF417 on the back of a licence or state ID from the live
// camera picture: passes run back to back while `active`, and the first payload
// the kiosk's AAMVA parser accepts is kept (latched); later passes stop.
//
// One read is enough. Unlike the passport's OCR, a PDF417 decode is exact or
// fails (Reed-Solomon error correction), so no two-read agreement is needed.
//
// Each pass tries the whole frame level, then the guide area level plus one
// correction angle that moves on every pass, so the whole sweep is covered every
// few passes without slowing any single one down (from the EN-4743 test page).
// The next pass starts only once the last one has answered, so a slow decode
// skips frames instead of queuing them (the kiosk's "don't fall behind" rule).
//
// Accuracy (plan 07): with the decoder in a Web Worker the passes run back to
// back, towards the kiosk's ~10 tries a second, capped only by the worker's
// speed. Only the on-page fallback keeps a 300 ms gap, so it can't hog the page.
// The guide area is decoded at 2400 px (scaled down from 4K, or also tried scaled
// up from a smaller stream), the whole
// frame at up to 1920 px, and every few passes the guide is tried with a
// hard-card variant: B (global histogram, faded / unevenly lit) or C (denoise,
// dusty / worn).
const MAIN_THREAD_PASS_INTERVAL_MS = 300;
const GUIDE_VARIANT_CYCLE: readonly DecodeVariant[] = ["A", "A", "B", "A", "A", "C"];

interface UseBarcodeScannerOptions {
  videoEl: HTMLVideoElement | null | undefined;
  stageEl: Element | null;
  guideEl: Element | null;
  active: boolean;
  // Called once, the moment a read is latched.
  onConfirmed?: (result: AamvaBarcode) => void;
}

export interface BarcodeScanStats {
  passes: number;
  // Decoded, but not a licence barcode (or no name in it).
  nonAamvaReads: number;
  // The decoder variant that read the card, null until then.
  variant: DecodeVariant | null;
  // Slowest pass so far (ms), and the stream size the last pass saw.
  slowestPassMs: number;
  videoWidth: number;
  videoHeight: number;
}

interface UseBarcodeScannerReturn {
  result: AamvaBarcode | null;
  // Clears the kept read so the next active period reads from scratch.
  reset: () => void;
  getStats: () => BarcodeScanStats;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useBarcodeScanner = ({
  videoEl,
  stageEl,
  guideEl,
  active,
  onConfirmed,
}: UseBarcodeScannerOptions): UseBarcodeScannerReturn => {
  const [result, setResult] = useState<AamvaBarcode | null>(null);
  // Bumped by reset() so the reading loop starts again even if `active` didn't change.
  const [generation, setGeneration] = useState<number>(0);
  const latchedRef = useRef<AamvaBarcode | null>(null);
  const passesRef = useRef<number>(0);
  const nonAamvaRef = useRef<number>(0);
  const sweepIndexRef = useRef<number>(1);
  const variantRef = useRef<DecodeVariant | null>(null);
  const slowestPassRef = useRef<number>(0);
  const sizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const onConfirmedRef = useRef(onConfirmed);
  useEffect(() => {
    onConfirmedRef.current = onConfirmed;
  }, [onConfirmed]);

  useEffect(() => {
    if (!active || !videoEl || !stageEl || !guideEl || latchedRef.current) return;
    let cancelled = false;

    const loop = async () => {
      if (!(await isPdf417ReaderReady()) || cancelled) return;
      while (!cancelled && !latchedRef.current) {
        const started = performance.now();
        const guide = guideInVideoPixels(videoEl, stageEl, guideEl);
        if (guide) {
          const extra = SWEEP_ANGLES[sweepIndexRef.current];
          sweepIndexRef.current = sweepIndexRef.current + 1 >= SWEEP_ANGLES.length ? 1 : sweepIndexRef.current + 1;
          const whole = { x: 0, y: 0, w: videoEl.videoWidth, h: videoEl.videoHeight };
          sizeRef.current = { w: whole.w, h: whole.h };
          const guideVariant = GUIDE_VARIANT_CYCLE[passesRef.current % GUIDE_VARIANT_CYCLE.length];
          // One request per pass; the decoding runs in the worker (pdf417Reader.ts).
          const { text, variant } = await decodePdf417(videoEl, [
            { region: whole, angles: [0], variant: "A", maxWidth: WHOLE_FRAME_MAX_WIDTH },
            { region: guide, angles: [0, extra], variant: guideVariant, maxWidth: MAX_DECODE_WIDTH, minWidth: MAX_DECODE_WIDTH },
          ]);
          if (cancelled) return;
          passesRef.current += 1;
          slowestPassRef.current = Math.max(slowestPassRef.current, Math.round(performance.now() - started));
          if (text) {
            const parsed = parseAamvaBarcode(text);
            if (parsed) {
              variantRef.current = variant;
              latchedRef.current = parsed;
              setResult(parsed);
              onConfirmedRef.current?.(parsed);
              return;
            }
            // Some other barcode in frame: keep scanning, as the kiosk does.
            nonAamvaRef.current += 1;
          }
        }
        // Always hand the page a turn between passes, even after a pass slower
        // than the interval, so the fallback on-page decoder can't freeze it
        // across passes.
        const interval = pdf417ReaderMode() === "main-thread" ? MAIN_THREAD_PASS_INTERVAL_MS : 0;
        await sleep(Math.max(0, interval - (performance.now() - started)));
      }
    };
    void loop();

    return () => {
      cancelled = true;
    };
  }, [active, videoEl, stageEl, guideEl, generation]);

  const reset = useCallback(() => {
    latchedRef.current = null;
    passesRef.current = 0;
    nonAamvaRef.current = 0;
    sweepIndexRef.current = 1;
    variantRef.current = null;
    slowestPassRef.current = 0;
    setResult(null);
    setGeneration((g) => g + 1);
  }, []);

  const getStats = useCallback(
    (): BarcodeScanStats => ({
      passes: passesRef.current,
      nonAamvaReads: nonAamvaRef.current,
      variant: variantRef.current,
      slowestPassMs: slowestPassRef.current,
      videoWidth: sizeRef.current.w,
      videoHeight: sizeRef.current.h,
    }),
    [],
  );

  return { result, reset, getStats };
};
